import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * `application/ld+json` bloklarını `<head>`'e yazar.
 *
 * Neden servis: JSON-LD daha önce bileşenlerin içinden global `document` ile
 * yazılıyordu (`document.createElement` + `document.head.appendChild`) ve bu
 * yüzden `isPlatformBrowser` guard'ı zorunluydu — sonuç olarak SSR HTML'inde
 * yapısal veri hiç bulunmuyordu. Burada `CanonicalService` ile aynı kalıp
 * kullanılıyor: `Renderer2` + enjekte edilen `DOCUMENT`, yani sunucuda da
 * çalışır ve platform guard'ı gerekmez.
 *
 * Etiketler `id` ile kimliklendirilir ve aynı `id` tekrar yazıldığında
 * değiştirilir. Bu iki durumu birlikte çözer: SSR'ın yazdığı blok hydration
 * sonrası bileşen tekrar çalıştığında ikizlenmez, ve SPA gezinmesinde önceki
 * sayfanın bloğu DOM'da birikmez.
 */
@Injectable({
  providedIn: 'root',
})
export class StructuredDataService {
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Verilen `id` altındaki JSON-LD bloğunu yazar ya da günceller.
   *
   * @param id `<script>` etiketinin `id` niteliği; sayfa başına sabit bir değer
   *           olmalı (ör. `btx-event-schema`).
   * @param payload Serileştirilecek schema.org nesnesi. `null`/`undefined` ya da
   *                boş dizi verilirse varsa mevcut blok kaldırılır — böylece
   *                verisi olmayan sayfa boş bir `{}` yayınlamaz.
   */
  setJsonLd(id: string, payload: unknown): void {
    if (this.isEmpty(payload)) {
      this.removeJsonLd(id);
      return;
    }

    const serialized = JSON.stringify(payload);
    const existing = this.document.getElementById(id);

    if (existing) {
      this.renderer.setProperty(existing, 'textContent', serialized);
      return;
    }

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setAttribute(script, 'id', id);
    this.renderer.setProperty(script, 'textContent', serialized);
    this.renderer.appendChild(this.document.head, script);
  }

  removeJsonLd(id: string): void {
    const existing = this.document.getElementById(id);
    if (existing) {
      this.renderer.removeChild(this.document.head, existing);
    }
  }

  private isEmpty(payload: unknown): boolean {
    if (payload === null || payload === undefined) {
      return true;
    }
    return Array.isArray(payload) && payload.length === 0;
  }
}
